// src/components/cards/FearGreedCard.tsx
import React from 'react';
import { FearGreedIndex } from '../../types';
import DataStatusIndicator, { DataStatus } from '../DataStatusIndicator';

interface FearGreedCardProps {
  data: FearGreedIndex | null;
  loading: boolean;
  error: string | null;
  lastUpdated: number;
  formatTimestamp: (timestamp: number) => string;
  dataStatus?: DataStatus;
}

const FearGreedCard: React.FC<FearGreedCardProps> = ({
  data,
  loading,
  error,
  lastUpdated,
  formatTimestamp,
  dataStatus = 'live'
}) => {
  // Get color based on classification
  const getColor = (classification?: string): string => {
    switch (classification) {
      case 'Extreme Fear':
        return 'var(--color-extreme-fear)';
      case 'Fear':
        return 'var(--color-fear)';
      case 'Neutral':
        return 'var(--color-neutral-sentiment)';
      case 'Greed':
        return 'var(--color-greed)';
      case 'Extreme Greed':
        return 'var(--color-extreme-greed)';
      default:
        return 'var(--color-neutral)';
    }
  };

  // Get change indicator
  const getChangeIndicator = () => {
    if (!data || data.change === undefined) return null;
    
    const isPositive = data.change > 0;
    const isNeutral = data.change === 0;
    
    return (
      <div className={`value-change ${isPositive ? 'value-change-positive' : isNeutral ? 'value-change-neutral' : 'value-change-negative'}`}>
        {isPositive ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 value-change-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        ) : isNeutral ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 value-change-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 value-change-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
        {Math.abs(data.change)} ({data.changePercentage ? Math.abs(data.changePercentage).toFixed(1) + '%' : ''})
      </div>
    );
  };

  return (
    <div className="bg-dashboard-card-light dark:bg-dashboard-card-dark rounded-card shadow-card p-5 dashboard-card relative">
      <DataStatusIndicator status={dataStatus} lastUpdated={lastUpdated} />
      
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-lg font-semibold">Fear & Greed Index</h3>
        {loading && (
          <div className="animate-pulse px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-xs">
            Updating...
          </div>
        )}
      </div>

      {error ? (
        <div className="text-red-500 py-8 text-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>{error}</p>
        </div>
      ) : data ? (
        <>
          {/* Value and Classification */}
          <div className="flex items-center justify-between mb-3">
            <div className="text-3xl font-bold">{data.value}</div>
            <div 
              className="text-lg font-medium px-3 py-1 rounded-full"
              style={{ 
                backgroundColor: `${getColor(data.classification)}20`,
                color: getColor(data.classification)
              }}
            >
              {data.classification}
            </div>
          </div>

          {/* Change Indicator */}
          <div className="mb-4">
            {getChangeIndicator()}
          </div>

          {/* Gauge */}
          <div className="fear-greed-gauge">
            <div 
              className="fear-greed-marker"
              style={{ left: `${data.value}%` }}
            ></div>
          </div>

          {/* Labels */}
          <div className="flex justify-between text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark mt-1">
            <div>Extreme Fear</div>
            <div>Neutral</div>
            <div>Extreme Greed</div>
          </div>

          {/* Last Updated */}
          <div className="mt-4 text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
            Last updated: {formatTimestamp(lastUpdated || data.timestamp)}
          </div>
        </>
      ) : (
        <div className="py-8 text-center text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>No data available</p>
        </div>
      )}
    </div>
  );
};

export default FearGreedCard;
